import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { docCategoryApi, docTagApi } from "@/services/docTaxonomy";
import SimpleSelect from "@/components/base/SimpleSelect";
import SimpleMultiSelect from "@/components/base/SimpleMultiSelect";
import CreatableMultiSelect from "@/components/base/CreatableMultiSelect";
import type { DocTagGroup } from "@/mocks/documentsData";
import type { SyncPathConfig } from "@/mocks/documentsData";
import StudyCodeSelect from "@/pages/admin/studies/components/StudyCodeSelect";
import NewStudyModal from "@/pages/admin/studies/components/NewStudyModal";
import { mockStudies, type Study } from "@/mocks/studiesData";
import { createStudy } from "@/services/studies";

function parseCommaInput(raw: string): string[] {
  return raw
    .split(/[,，\n]+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function pathsToText(paths: string[]): string {
  return paths.join(", ");
}

// ── Preset tag values by tag name ─────────────────────────────────────────
const TAG_PRESET_VALUES: Record<string, string[]> = {
  Region: ["Mainland China", "Hong Kong"],
  Indication: ["Oncology"],
  "Study Code": ["PACIFIC"],
  Language: ["English"],
  Audience: ["CRA"],
};

function hasPresetValues(tagName: string): boolean {
  return !!TAG_PRESET_VALUES[tagName] && TAG_PRESET_VALUES[tagName].length > 0;
}

function getPresetValues(tagName: string): string[] {
  return TAG_PRESET_VALUES[tagName] ?? [];
}

export interface MVRSourceFormData {
  studyCode: string;
  studyName: string;
  channelUrl: string;
  description: string;
  docCategories: string[];
  docTagGroups: DocTagGroup[];
  agentTips: string;
  includePaths: string[];
  excludePaths: string[];
  visibleToUsers: boolean;
  connectionType: "teams" | "sharepoint";
  isUserListSource: boolean;
}

interface Props {
  mode: "add" | "edit";
  connectionType: "teams" | "sharepoint";
  existingSources?: SyncPathConfig[];
  initialStudyCode?: string;
  initialStudyName?: string;
  initialChannelUrl?: string;
  initialDescription?: string;
  initialDocCategories?: string[];
  initialDocTagGroups?: DocTagGroup[];
  initialAgentTips?: string;
  initialIncludePaths?: string[];
  initialExcludePaths?: string[];
  initialVisibleToUsers?: boolean;
  initialIsUserListSource?: boolean;
  onCancel: () => void;
  onConfirm: (data: MVRSourceFormData) => void;
}

export default function MVRSourceFormPage({
  mode,
  connectionType,
  existingSources = [],
  initialStudyCode,
  initialStudyName,
  initialChannelUrl,
  initialDescription,
  initialDocCategories,
  initialDocTagGroups,
  initialAgentTips,
  initialIncludePaths,
  initialExcludePaths,
  initialVisibleToUsers,
  initialIsUserListSource,
  onCancel,
  onConfirm,
}: Props) {
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === "zh";
  const isEdit = mode === "edit";

  const [studyCode, setStudyCode] = useState(initialStudyCode ?? "");
  const [studyName, setStudyName] = useState(initialStudyName ?? "");
  const [channelUrl, setChannelUrl] = useState(initialChannelUrl ?? "");
  const [description, setDescription] = useState(initialDescription ?? "");
  const [docCategories, setDocCategories] = useState<string[]>(initialDocCategories ?? []);
  const [docTagGroups, setDocTagGroups] = useState<DocTagGroup[]>(
    initialDocTagGroups ?? (mode === "add" ? [{ tagType: "", tags: [] }] : []),
  );
  const [agentTips, setAgentTips] = useState(initialAgentTips ?? "");
  const [includePathsText, setIncludePathsText] = useState(
    initialIncludePaths ? pathsToText(initialIncludePaths) : "",
  );
  const [excludePathsText, setExcludePathsText] = useState(
    initialExcludePaths ? pathsToText(initialExcludePaths) : "",
  );
  const [visibleToUsers, setVisibleToUsers] = useState(initialVisibleToUsers ?? false);
  const [isUserListSource, setIsUserListSource] = useState(initialIsUserListSource ?? false);
  const [showNewStudy, setShowNewStudy] = useState(false);

  // ── Validation helpers ───────────────────────────────────────────────────
  const sameTypeConflict = useMemo(() => {
    if (!studyCode || mode === "edit") return false;
    return existingSources.some(
      (s) => s.studyCode === studyCode && s.connectionType === connectionType,
    );
  }, [studyCode, connectionType, existingSources, mode]);

  const otherUserListSource = useMemo(() => {
    if (!studyCode) return null;
    return (
      existingSources.find(
        (s) =>
          s.studyCode === studyCode &&
          s.isUserListSource &&
          (mode === "add" ||
            s.studyCode !== initialStudyCode ||
            s.channelUrl !== initialChannelUrl),
      ) ?? null
    );
  }, [studyCode, existingSources, mode, initialStudyCode, initialChannelUrl]);

  const userListDisabled = !!otherUserListSource;

  // ── Load categories and tags from Admin Mgmt ────────────────────────────
  const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
  const [tagOptions, setTagOptions] = useState<string[]>([]);
  const [tagPrompts, setTagPrompts] = useState<Record<string, string>>();
  const [loadingMeta, setLoadingMeta] = useState(true);

  useEffect(() => {
    const loadMeta = async () => {
      setLoadingMeta(true);
      try {
        const [categories, tags] = await Promise.all([docCategoryApi.list(), docTagApi.list()]);

        setCategoryOptions(categories.map((c) => c.name).sort((a, b) => a.localeCompare(b)));

        // 仅 Custom 标签可绑定到 Source（沿用接入后端前的既有行为）
        const customTags = tags
          .filter((tg) => tg.is_deletable)
          .sort((a, b) => a.name.localeCompare(b.name));
        setTagOptions(customTags.map((tg) => tg.name));
        const prompts: Record<string, string> = {};
        customTags.forEach((tg) => {
          prompts[tg.name] = tg.prompt ?? "";
        });
        setTagPrompts(prompts);
      } finally {
        setLoadingMeta(false);
      }
    };
    loadMeta();
  }, []);

  const canConfirm =
    studyCode.trim().length > 0 &&
    channelUrl.trim().length > 0 &&
    includePathsText.trim().length > 0;

  const updateDocTagGroup = (index: number, tagType: string, tags: string[]) => {
    setDocTagGroups((prev) => {
      const next = [...prev];
      next[index] = { tagType, tags };
      return next;
    });
  };

  const addDocTagGroup = () => {
    setDocTagGroups((prev) => [...prev, { tagType: "", tags: [] }]);
  };

  const removeDocTagGroup = (index: number) => {
    setDocTagGroups((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    const parsedIncludes = parseCommaInput(includePathsText);
    const parsedExcludes = parseCommaInput(excludePathsText);
    if (parsedIncludes.length === 0) return;

    const study = mockStudies.find((s) => s.code === studyCode);
    const resolvedName = study?.name ?? studyCode;

    onConfirm({
      studyCode: studyCode.trim(),
      studyName: resolvedName,
      channelUrl: channelUrl.trim(),
      description: description.trim(),
      docCategories,
      docTagGroups,
      agentTips: agentTips.trim(),
      includePaths: parsedIncludes,
      excludePaths: parsedExcludes,
      visibleToUsers,
      connectionType,
      isUserListSource,
    });
  };

  const connectionLabel =
    connectionType === "teams"
      ? isZh
        ? "Teams Channel"
        : "Teams Channel"
      : isZh
        ? "SharePoint Site"
        : "SharePoint Site";

  const connectionIcon = connectionType === "teams" ? "ri-team-line" : "ri-cloud-line";

  return (
    <div className="">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={onCancel}
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 cursor-pointer whitespace-nowrap transition-colors mb-3"
        >
          <i className="ri-arrow-left-line text-base"></i>
          {isZh ? "返回来源列表" : "Back"}
        </button>
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-1.5">
            {isEdit
              ? isZh
                ? "编辑 Source"
                : "Edit Source"
              : isZh
                ? "添加 Source"
                : "Add Study Source"}
            <span className="relative group cursor-help">
              <i className="ri-question-line text-gray-400 text-sm w-4 h-4 flex items-center justify-center"></i>
              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                {isEdit
                  ? isZh
                    ? "修改 Source 链接、Study 关联及路径配置"
                    : "Modify source URL, study association, and path configuration"
                  : isZh
                    ? "添加新的 Teams / SharePoint 文档来源，并配置同步路径"
                    : "Add a new Teams / SharePoint document source and configure sync paths"}
              </span>
            </span>
          </h2>
          {/* Connection Type Badge */}
          <span
            className="inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
            style={{
              backgroundColor:
                connectionType === "teams" ? "rgba(131,0,81,0.08)" : "rgba(37,99,235,0.08)",
              color: connectionType === "teams" ? "#830051" : "#2563eb",
              border: `1px solid ${connectionType === "teams" ? "rgba(131,0,81,0.15)" : "rgba(37,99,235,0.15)"}`,
            }}
          >
            <i className={`${connectionIcon} text-xs`}></i>
            {connectionLabel}
          </span>
        </div>
      </div>

      {/* Validation warnings */}
      {sameTypeConflict && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-start gap-2">
          <i className="ri-error-warning-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
          <div>
            <p className="text-xs font-medium text-amber-800">
              {isZh ? "Connection 类型冲突" : "Connection Type Conflict"}
            </p>
            <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
              {isZh
                ? `该 Study 已有 1 个 ${connectionLabel}，每个 Study 最多只能绑定 1 个 Teams Channel 和 1 个 SharePoint Site。`
                : `This study already has a ${connectionLabel}. Each study can only have 1 Teams Channel and 1 SharePoint Site.`}
            </p>
          </div>
        </div>
      )}

      {/* Loading overlay for meta data */}
      {loadingMeta && (
        <div className="mb-4 p-3 rounded-lg border border-amber-200 bg-amber-50 flex items-center gap-2">
          <i className="ri-loader-4-line text-amber-500 text-sm animate-spin"></i>
          <span className="text-xs text-amber-700">
            Loading document metadata from Admin Mgmt...
          </span>
        </div>
      )}

      {/* Form Body */}
      <div className="flex gap-6">
        {/* Left Column - Fields */}
        <div className="flex-1 flex flex-col gap-5">
          {/* Study Select */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "Study" : "Study"} <span className="text-red-400">*</span>
                </label>
              </div>
              <div className="flex-1">
                <StudyCodeSelect
                  value={studyCode}
                  studyName={studyName}
                  onChange={(code) => {
                    const study = mockStudies.find((s) => s.code === code);
                    setStudyCode(code);
                    setStudyName(study?.name ?? code);
                  }}
                  onNewStudy={() => setShowNewStudy(true)}
                  hasError={!studyCode.trim()}
                />
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "描述" : "Description"} <span className="text-red-400">*</span>
                </label>
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  placeholder={
                    isZh
                      ? "可选描述，帮助用户了解此来源的内容"
                      : "Optional description for users to understand this source"
                  }
                  className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2 outline-none focus:border-gray-400 transition-colors"
                />
              </div>
            </div>
          </div>

          {/* Document Categories */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  Doc Categories
                </label>
                <span className="relative group cursor-help">
                  <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                    Select document categories from Admin "Document Category Management" During
                    document analysis, files under this path will be classified into the selected
                    categories.
                  </span>
                </span>
              </div>
              <div className="flex-1">
                <SimpleMultiSelect
                  label=""
                  values={docCategories}
                  options={categoryOptions}
                  onChange={setDocCategories}
                  placeholder="Select categories from Admin Mgmt..."
                  hideLabel
                />
                {categoryOptions.length === 0 && !loadingMeta && (
                  <p className="text-[11px] text-amber-600 mt-1">
                    No categories defined yet. Please go to Admin &rarr; Source Settings &rarr;
                    Document Categories to create them first.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Document Tags */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  Doc Tags
                </label>
                <span className="relative group cursor-help">
                  <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                    Select tags from Admin "Document Tag Management" and define tag values. During
                    document analysis, files under this path will be tagged accordingly.
                  </span>
                </span>
              </div>
              <div className="flex-1 flex flex-col gap-3">
                {docTagGroups.map((group, index) => (
                  <div key={index} className="flex items-start gap-2">
                    <div className="w-40 flex-shrink-0">
                      <SimpleSelect
                        label=""
                        value={group.tagType}
                        options={tagOptions}
                        onChange={(val) => updateDocTagGroup(index, val, group.tags)}
                        placeholder="Select tag..."
                        hideLabel
                      />
                    </div>
                    <div className="flex-1 flex flex-col gap-1">
                      {hasPresetValues(group.tagType) ? (
                        <SimpleMultiSelect
                          label=""
                          values={group.tags}
                          options={getPresetValues(group.tagType)}
                          onChange={(vals) => updateDocTagGroup(index, group.tagType, vals)}
                          placeholder={`Select ${group.tagType.toLowerCase()}(s) or type custom...`}
                          hideLabel
                          creatable
                        />
                      ) : (
                        <CreatableMultiSelect
                          label=""
                          values={group.tags}
                          options={[]}
                          onChange={(vals) => updateDocTagGroup(index, group.tagType, vals)}
                          placeholder={
                            group.tagType && tagPrompts?.[group.tagType]
                              ? `e.g. ${tagPrompts[group.tagType].split("。")[0].slice(0, 60)}...`
                              : "Tag values..."
                          }
                          hideLabel
                        />
                      )}
                      {group.tagType && tagPrompts?.[group.tagType] && (
                        <p className="text-[10px] text-gray-400 leading-relaxed px-0.5">
                          {tagPrompts[group.tagType]}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => removeDocTagGroup(index)}
                      className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 cursor-pointer transition-colors mt-0.5"
                      title="Remove this tag group"
                    >
                      <i className="ri-delete-bin-line text-sm text-red-400"></i>
                    </button>
                  </div>
                ))}
                <button
                  onClick={addDocTagGroup}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 cursor-pointer whitespace-nowrap transition-colors"
                >
                  <i className="ri-add-line text-sm"></i>
                  Add tag
                </button>
                {tagOptions.length === 0 && !loadingMeta && (
                  <p className="text-[11px] text-amber-600">
                    No tags defined yet. Please go to Admin &rarr; Source Settings &rarr; Document
                    Tags to create them first.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Agent Tips */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-1.5">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "Agent Tips" : "Agent Tips"}
                </label>
                <span className="relative group cursor-help">
                  <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-64 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-10 leading-relaxed">
                    {isZh
                      ? "为 AI Agent 提供的提示，帮助它在回答问题时更好地理解和利用此来源"
                      : "Instructions for the AI agent to better understand this source. These tips help guide how the AI uses and interprets this source in responses."}
                  </span>
                </span>
              </div>
              <div className="flex-1">
                <textarea
                  value={agentTips}
                  onChange={(e) => setAgentTips(e.target.value)}
                  placeholder={
                    isZh
                      ? "例如：优先使用最新版本的文档。对于合规性问题，始终引用具体的法规文件。"
                      : "e.g. Prioritize current documents over archived ones. For compliance queries, always cite the specific regulation document."
                  }
                  rows={4}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 bg-white resize-none transition-colors"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {isZh
                    ? "这些提示将帮助 AI Agent 引导如何解释和使用此来源"
                    : "These tips are used by the AI agent to guide how it interprets and uses this source"}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* Channel URL */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-2">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "Source 链接" : "Source URL"} <span className="text-red-400">*</span>
                </label>
              </div>
              <div className="flex-1 flex flex-col gap-1">
                <input
                  type="text"
                  value={channelUrl}
                  onChange={(e) => setChannelUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
                  placeholder={
                    connectionType === "teams"
                      ? "https://teams.microsoft.com/l/channel/..."
                      : "https://astrazeneca.sharepoint.com/sites/..."
                  }
                  className="w-full text-sm border border-gray-200 rounded-lg px-3.5 py-2 outline-none focus:border-gray-400 font-mono transition-colors"
                />
                <p className="text-[10px] text-gray-400 leading-relaxed">
                  {connectionType === "teams"
                    ? isZh
                      ? "在 Teams 中右键点击 Channel → 「获取 Channel 链接」即可复制"
                      : 'In Teams, right-click the Channel → "Get link to channel" to copy'
                    : isZh
                      ? "在 SharePoint 中复制 Site URL，确保 AZ IT 已完成授权"
                      : "Copy the SharePoint Site URL. Ensure AZ IT has completed authorization."}
                </p>
              </div>
            </div>
          </div>

          {/* Include Paths */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-1.5">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "Include Paths" : "Include Paths"} <span className="text-red-400">*</span>
                </label>
              </div>
              <div className="flex-1">
                <textarea
                  value={includePathsText}
                  onChange={(e) => setIncludePathsText(e.target.value)}
                  placeholder={
                    isZh
                      ? "粘贴路径，用逗号或换行分隔，例如：01_Protocol, 02_ICF"
                      : "Paste paths separated by commas or newlines, e.g. 01_Protocol, 02_ICF"
                  }
                  rows={4}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 font-mono bg-white resize-none transition-colors"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {isZh
                    ? "用逗号或换行分隔多个路径"
                    : "Separate multiple paths with commas or newlines"}
                </p>
              </div>
            </div>
          </div>

          {/* Exclude Paths */}
          <div className="flex flex-col gap-2">
            <div className="flex items-start gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0 pt-1.5">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "Exclude Paths" : "Exclude Paths"}
                </label>
              </div>
              <div className="flex-1">
                <textarea
                  value={excludePathsText}
                  onChange={(e) => setExcludePathsText(e.target.value)}
                  placeholder={
                    isZh
                      ? "粘贴排除路径，用逗号分隔，例如：/Drafts, /Archived"
                      : "Paste excluded paths separated by commas, e.g. /Drafts, /Archived"
                  }
                  rows={4}
                  className="w-full text-xs border border-gray-200 rounded-lg px-3.5 py-2.5 outline-none focus:border-gray-400 font-mono bg-white resize-none transition-colors"
                />
                <p className="text-[10px] text-gray-400 mt-1">
                  {isZh
                    ? "这些路径将被排除在 Include Paths 之外"
                    : "These paths will be excluded from all include paths"}
                </p>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-gray-100"></div>

          {/* User List Sync */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "同步用户权限" : "User List Sync"}
                </label>
                <span className="relative group cursor-help">
                  <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-60 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                    {isZh
                      ? "开启后，系统将从此 Connection 同步用户权限列表。每个 Study 只能有 1 个 Connection 用于同步用户权限。"
                      : "When enabled, user permissions will be synced from this connection. Each study can only have 1 connection for user list sync."}
                  </span>
                </span>
              </div>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <label
                  className={`flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none ${userListDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  <span
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      isUserListSource && !userListDisabled
                        ? "border-[#830051] bg-[#830051]"
                        : userListDisabled
                          ? "border-gray-200 bg-gray-100"
                          : "border-gray-300 bg-white"
                    }`}
                  >
                    {isUserListSource && !userListDisabled && (
                      <i className="ri-check-line text-white text-[10px]"></i>
                    )}
                  </span>
                  <input
                    type="checkbox"
                    checked={isUserListSource}
                    onChange={() => {
                      if (!userListDisabled) setIsUserListSource(!isUserListSource);
                    }}
                    disabled={userListDisabled}
                    className="sr-only"
                  />
                  {isZh
                    ? "从此 Connection 同步用户权限"
                    : "Sync user permissions from this connection"}
                </label>
              </div>
            </div>
            {userListDisabled && otherUserListSource && (
              <div className="flex items-start gap-4">
                <div className="w-36 flex-shrink-0"></div>
                <p className="text-[11px] text-amber-600 leading-relaxed">
                  {isZh
                    ? `该 Study 已有 "${otherUserListSource.channelName}" 用于同步用户权限，每个 Study 只能二选一。`
                    : `"${otherUserListSource.channelName}" is already syncing user permissions for this study. Only one connection can be selected.`}
                </p>
              </div>
            )}
          </div>

          {/* User Visibility / User Scope */}
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 w-36 flex-shrink-0">
                <label className="text-sm font-semibold text-gray-800 whitespace-nowrap">
                  {isZh ? "用户可见" : "User Scope"}
                </label>
                <span className="relative group cursor-help">
                  <i className="ri-question-line text-gray-400 text-xs w-4 h-4 flex items-center justify-center"></i>
                  <span className="absolute left-full top-1/2 -translate-y-1/2 ml-1.5 hidden group-hover:block w-60 text-[11px] text-gray-600 bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-1.5 z-50 leading-relaxed">
                    {isZh
                      ? "勾选后，该来源将对所有用户可见。未勾选则仅限管理员可见。"
                      : "When checked, this source will be visible to all users. When unchecked, it will only be visible to administrators."}
                  </span>
                </span>
              </div>
              <div className="flex-1 flex items-center gap-3 min-w-0">
                <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700 select-none">
                  <span
                    className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                      visibleToUsers ? "border-[#830051] bg-[#830051]" : "border-gray-300 bg-white"
                    }`}
                  >
                    {visibleToUsers && <i className="ri-check-line text-white text-[10px]"></i>}
                  </span>
                  <input
                    type="checkbox"
                    checked={visibleToUsers}
                    onChange={() => setVisibleToUsers(!visibleToUsers)}
                    className="sr-only"
                  />
                  {isZh ? "对外可见" : "External"}
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column - Sidebar */}
        <div className="w-[300px] flex-shrink-0">
          <div className="flex flex-col gap-3">
            {/* Connection summary */}
            <div className="flex items-start gap-3 p-4 rounded-xl border border-gray-100 bg-white">
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${connectionType === "teams" ? "bg-[rgba(131,0,81,0.06)]" : "bg-blue-50"}`}
              >
                <i
                  className={`${connectionIcon} text-base ${connectionType === "teams" ? "text-[#830051]" : "text-blue-500"}`}
                ></i>
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-800">{connectionLabel}</p>
                <p className="text-[11px] text-gray-500 mt-0.5 leading-relaxed">
                  {connectionType === "teams"
                    ? isZh
                      ? "Teams Channel 用于实时文档协作与同步。"
                      : "Teams Channel for real-time document collaboration and sync."
                    : isZh
                      ? "SharePoint Site 用于结构化文档存储与权限管理。"
                      : "SharePoint Site for structured document storage and permission management."}
                </p>
              </div>
            </div>

            {/* Authorization warning */}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-100">
              <i className="ri-error-warning-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
              <div>
                <p className="text-xs font-semibold text-amber-800">
                  {isZh ? "需要 AZ IT 授权" : "AZ IT Authorization Required"}
                </p>
                <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                  {isZh
                    ? "该 Source 必须由 AZ IT 对系统 client_id 完成授权，否则文件夹内容无法被索引。请先联系 AZ IT 完成授权后再继续操作。"
                    : "The source must be authorized by AZ IT for this system's client_id before folders can be indexed. Contact AZ IT to complete the authorization before proceeding."}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="flex items-center justify-end gap-3 mt-8 pt-5 border-t border-gray-100">
        <button
          onClick={onCancel}
          className="text-sm px-5 py-2 rounded-lg border border-gray-200 text-gray-500 cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
        >
          {isZh ? "取消" : "Cancel"}
        </button>
        <button
          onClick={handleSubmit}
          disabled={!canConfirm}
          className="text-sm px-6 py-2 rounded-lg text-white cursor-pointer whitespace-nowrap transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ backgroundColor: "#830051" }}
        >
          {isEdit ? (isZh ? "保存修改" : "Save Changes") : isZh ? "保存" : "Save"}
        </button>
      </div>

      {/* New Study Modal */}
      {showNewStudy && (
        <NewStudyModal
          onClose={() => setShowNewStudy(false)}
          onSave={async (input) => {
            const study = await createStudy(input);
            mockStudies.push({
              ...study,
              cra: study.assignedCras.map((user) => user.name),
              status: study.status as Study["status"],
            });
            setStudyCode(study.code);
            setStudyName(study.name);
            setShowNewStudy(false);
          }}
        />
      )}
    </div>
  );
}
